import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, 
  Clock, 
  ExternalLink, 
  Ticket,
  Navigation,
  Building2,
  UtensilsCrossed,
  Landmark,
  Baby,
  Moon,
  Bus,
  Info,
  Download,
  ArrowRight
} from 'lucide-react';

const LocalGuide = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All', icon: MapPin },
    { id: 'restaurants', name: 'Restaurants', icon: UtensilsCrossed },
    { id: 'museums', name: 'Museums', icon: Building2 },
    { id: 'sightseeing', name: 'Sightseeing', icon: Landmark },
    { id: 'family', name: 'Family-Friendly', icon: Baby },
    { id: 'nightlife', name: 'Nightlife', icon: Moon },
    { id: 'tours', name: 'Tours', icon: Bus },
    { id: 'transport', name: 'Getting Around', icon: Navigation },
  ];

  const museums = [
    {
      name: 'Smithsonian National Air and Space Museum',
      description: 'World\'s largest collection of aviation and space artifacts with free admission.',
      neighborhood: 'National Mall',
      distance: '15-20 min',
      website: 'https://airandspace.si.edu/',
      tickets: 'https://airandspace.si.edu/visit',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'museums'
    },
    {
      name: 'National Museum of African American History & Culture',
      description: 'Powerful museum documenting African American history and culture.',
      neighborhood: 'National Mall',
      distance: '15-20 min',
      website: 'https://nmaahc.si.edu/',
      tickets: 'https://nmaahc.si.edu/visit/passes',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'museums'
    },
    {
      name: 'National Museum of Natural History',
      description: 'Iconic museum featuring dinosaurs, gems, and natural wonders.',
      neighborhood: 'National Mall',
      distance: '15-20 min',
      website: 'https://naturalhistory.si.edu/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'museums'
    },
    {
      name: 'National Gallery of Art',
      description: 'World-class art museum with masterpieces from classical to modern.',
      neighborhood: 'National Mall',
      distance: '15-20 min',
      website: 'https://www.nga.gov/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'museums'
    },
    {
      name: 'United States Holocaust Memorial Museum',
      description: 'Moving tribute and educational resource about the Holocaust.',
      neighborhood: 'Southwest DC',
      distance: '10-15 min',
      website: 'https://www.ushmm.org/',
      tickets: 'https://www.ushmm.org/visit/plan-your-visit',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'museums'
    },
    {
      name: 'International Spy Museum',
      description: 'Interactive museum exploring espionage history and techniques.',
      neighborhood: 'L\'Enfant Plaza',
      distance: '10-15 min',
      website: 'https://www.spymuseum.org/',
      tickets: 'https://www.spymuseum.org/tickets/',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'museums'
    },
    {
      name: 'The Phillips Collection',
      description: 'America\'s first museum of modern art in an intimate setting.',
      neighborhood: 'Dupont Circle',
      distance: '10-15 min',
      website: 'https://www.phillipscollection.org/',
      tickets: 'https://www.phillipscollection.org/visit',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'museums'
    },
  ];

  const sightseeing = [
    {
      name: 'The National Mall',
      description: 'Iconic 2-mile park featuring monuments, memorials, and museums.',
      neighborhood: 'Central DC',
      distance: '10-20 min',
      website: 'https://www.nps.gov/nama/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'sightseeing'
    },
    {
      name: 'U.S. Capitol',
      description: 'Home of Congress with stunning architecture and free tours available.',
      neighborhood: 'Capitol Hill',
      distance: '5-15 min',
      website: 'https://www.visitthecapitol.gov/',
      tickets: 'https://tours.visitthecapitol.gov/',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'sightseeing'
    },
    {
      name: 'The White House',
      description: 'Iconic presidential residence. Tours require advance request.',
      neighborhood: 'Downtown',
      distance: '10-15 min',
      website: 'https://www.whitehouse.gov/about-the-white-house/tours-events/',
      tickets: 'https://www.whitehouse.gov/about-the-white-house/tours-events/',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'sightseeing'
    },
    {
      name: 'Lincoln Memorial',
      description: 'Majestic tribute to President Lincoln overlooking the Reflecting Pool.',
      neighborhood: 'National Mall',
      distance: '15-20 min',
      website: 'https://www.nps.gov/linc/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'sightseeing'
    },
    {
      name: 'Washington Monument',
      description: 'Iconic obelisk honoring George Washington. Reserve tickets in advance.',
      neighborhood: 'National Mall',
      distance: '15-20 min',
      website: 'https://www.nps.gov/wamo/',
      tickets: 'https://www.recreation.gov/ticket/facility/253731',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'sightseeing'
    },
    {
      name: 'Martin Luther King, Jr. Memorial',
      description: 'Powerful memorial honoring Dr. King and the Civil Rights Movement.',
      neighborhood: 'National Mall',
      distance: '15-20 min',
      website: 'https://www.nps.gov/mlkm/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'sightseeing'
    },
    {
      name: 'Tidal Basin & Cherry Blossoms',
      description: 'Stunning waterfront walk, especially beautiful during spring bloom (March-April).',
      neighborhood: 'Southwest DC',
      distance: '15-20 min',
      website: 'https://www.nps.gov/subjects/cherryblossom/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'sightseeing'
    },
    {
      name: 'Georgetown Waterfront',
      description: 'Historic waterfront with dining, shopping, and scenic river views.',
      neighborhood: 'Georgetown',
      distance: '10-20 min',
      website: 'https://www.georgetowndc.com/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'sightseeing'
    },
    {
      name: 'The Wharf',
      description: 'Modern waterfront destination with restaurants, shops, and entertainment.',
      neighborhood: 'Southwest Waterfront',
      distance: '10-15 min',
      website: 'https://www.wharfdc.com/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'sightseeing'
    },
  ];

  const restaurants = [
    {
      name: 'Founding Farmers',
      description: 'Farm-to-table American cuisine with sustainable, locally-sourced ingredients.',
      neighborhood: 'Foggy Bottom',
      distance: '10-15 min',
      website: 'https://www.wearefoundingfarmers.com/locations/dc-foggy-bottom/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'restaurants'
    },
    {
      name: 'Old Ebbitt Grill',
      description: 'Historic DC institution serving classic American fare since 1856.',
      neighborhood: 'Downtown',
      distance: '10-15 min',
      website: 'https://www.ebbitt.com/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'restaurants'
    },
    {
      name: 'Rasika',
      description: 'Award-winning modern Indian cuisine in an elegant setting.',
      neighborhood: 'Penn Quarter',
      distance: '10-15 min',
      website: 'https://www.rasikarestaurant.com/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'restaurants'
    },
    {
      name: 'The Dabney',
      description: 'Mid-Atlantic cuisine cooked over a wood hearth with seasonal ingredients.',
      neighborhood: 'Shaw',
      distance: '10-15 min',
      website: 'https://www.thedabney.com/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'restaurants'
    },
    {
      name: 'Le Diplomate',
      description: 'Bustling French brasserie with authentic Parisian atmosphere.',
      neighborhood: '14th Street',
      distance: '10-15 min',
      website: 'https://lediplomatedc.com/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'restaurants'
    },
    {
      name: 'Farmers Fishers Bakers',
      description: 'Waterfront dining with farm-fresh American food and stunning views.',
      neighborhood: 'Georgetown',
      distance: '15-20 min',
      website: 'https://www.farmersfishersbakers.com/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'restaurants'
    },
    {
      name: 'Mi Vida',
      description: 'Upscale Mexican restaurant at The Wharf with rooftop views.',
      neighborhood: 'The Wharf',
      distance: '10-15 min',
      website: 'https://www.mividarestaurant.com/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'restaurants'
    },
    {
      name: 'Hank\'s Oyster Bar',
      description: 'New England-style seafood in a casual, welcoming atmosphere.',
      neighborhood: 'Dupont Circle',
      distance: '10-15 min',
      website: 'https://www.hanksoysterbar.com/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'restaurants'
    },
  ];

  const familyFriendly = [
    {
      name: 'Smithsonian Museums (Kid-Friendly)',
      description: 'Free museums perfect for families: Natural History, Air & Space, and more.',
      neighborhood: 'National Mall',
      distance: '15-20 min',
      website: 'https://www.si.edu/visit',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'family'
    },
    {
      name: 'Smithsonian National Zoo',
      description: 'Free zoo featuring giant pandas, elephants, and 2,700 animals.',
      neighborhood: 'Woodley Park',
      distance: '15-20 min',
      website: 'https://nationalzoo.si.edu/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'family'
    },
    {
      name: 'International Spy Museum',
      description: 'Interactive exhibits kids love exploring espionage and spy gadgets.',
      neighborhood: 'L\'Enfant Plaza',
      distance: '10-15 min',
      website: 'https://www.spymuseum.org/',
      tickets: 'https://www.spymuseum.org/tickets/',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'family'
    },
    {
      name: 'The Wharf Boardwalk',
      description: 'Family-friendly waterfront with splash park (seasonal) and activities.',
      neighborhood: 'Southwest Waterfront',
      distance: '10-15 min',
      website: 'https://www.wharfdc.com/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'family'
    },
    {
      name: 'Yards Park',
      description: 'Modern park with splash pad, boardwalk, and family events (seasonal).',
      neighborhood: 'Capitol Riverfront',
      distance: '10-15 min',
      website: 'https://www.yardspark.org/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'family'
    },
  ];

  // Nightlife - Top Rooftop Bars and Party Lounges in Washington DC
  const nightlife = [
    {
      name: 'Onyx Rooftop Lounge',
      description: 'One of the best rooftop bars in Washington DC, Onyx Rooftop Lounge offers stunning panoramic views of the city skyline paired with a vibrant party atmosphere. Known for its energetic music, craft cocktails, and upscale party vibe, it\'s a top destination for visitors and groups looking to experience DC nightlife at its finest. Perfect for those staying at Capitol View Stays vacation rentals who want a memorable night out near Capitol Hill.',
      neighborhood: 'Downtown DC',
      distance: '10-15 min',
      website: 'https://www.onyxrooftopdc.com/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'nightlife'
    },
    {
      name: 'POV Rooftop Lounge',
      description: 'Located atop the W Hotel, POV offers breathtaking views of the White House and Washington Monument. This upscale rooftop bar is perfect for craft cocktails and a sophisticated DC nightlife experience.',
      neighborhood: 'Downtown',
      distance: '10-15 min',
      website: 'https://www.whashingtondc.com/pov-rooftop-lounge',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'nightlife'
    },
    {
      name: 'Top of the Gate',
      description: 'Perched above the Watergate Hotel, this iconic rooftop bar offers stunning Potomac River views and elegant cocktails. A must-visit for those exploring Washington DC nightlife.',
      neighborhood: 'Foggy Bottom',
      distance: '15-20 min',
      website: 'https://www.thewatergatehotel.com/dine/top-of-the-gate',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'nightlife'
    },
    {
      name: 'Decades DC',
      description: 'High-energy nightclub with multiple floors playing music from different decades. One of the best places to party in Washington DC for groups and visitors looking for a fun night out.',
      neighborhood: 'Dupont Circle',
      distance: '10-15 min',
      website: 'https://www.decadesdc.com/',
      tickets: 'https://www.decadesdc.com/bottle-service',
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'nightlife'
    },
    {
      name: 'Echostage',
      description: 'DC\'s premier live music and nightclub venue hosting world-class DJs and artists. The ultimate destination for party lounges in DC and electronic music fans.',
      neighborhood: 'Northeast DC',
      distance: '20-25 min',
      website: 'https://www.echostage.com/',
      tickets: 'https://www.echostage.com/events',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'nightlife'
    },
    {
      name: 'Flash',
      description: 'Intimate underground club known for world-class DJ sets and an inclusive dance floor. A favorite among locals seeking authentic Washington DC nightlife near Capitol Hill.',
      neighborhood: 'Shaw',
      distance: '10-15 min',
      website: 'https://www.flashdc.com/',
      tickets: 'https://www.flashdc.com/events',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'nightlife'
    },
    {
      name: 'The Wharf District',
      description: 'DC\'s waterfront entertainment district with multiple bars, live music venues, and nightlife options. Perfect for groups staying at Washington DC vacation rentals looking for variety.',
      neighborhood: 'Southwest Waterfront',
      distance: '10-15 min',
      website: 'https://www.wharfdc.com/',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'nightlife'
    },
    {
      name: 'U Street Corridor',
      description: 'Historic neighborhood famous for live jazz, dive bars, and trendy cocktail lounges. The heart of nightlife near Capitol Hill with venues for every taste.',
      neighborhood: 'U Street',
      distance: '10-15 min',
      website: 'https://www.washington.org/visit-dc/u-street-corridor-guide',
      tickets: null,
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'nightlife'
    },
  ];

  const tours = [
    {
      name: 'DC Monuments at Night Tour',
      description: 'Guided evening tour of illuminated monuments and memorials.',
      neighborhood: 'National Mall',
      distance: 'Pickup available',
      website: 'https://www.viator.com/tours/Washington-DC/Washington-DC-Monuments-by-Night-Tour/d657-5197DCNIGHT',
      tickets: 'https://www.viator.com/tours/Washington-DC/Washington-DC-Monuments-by-Night-Tour/d657-5197DCNIGHT',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'tours'
    },
    {
      name: 'Big Bus Hop-On Hop-Off Tour',
      description: 'Flexible sightseeing with stops at major attractions throughout DC.',
      neighborhood: 'City-wide',
      distance: 'Multiple stops',
      website: 'https://www.bigbustours.com/en/washington-dc/washington-dc-bus-tours/',
      tickets: 'https://www.bigbustours.com/en/washington-dc/washington-dc-bus-tours/',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'tours'
    },
    {
      name: 'Potomac River Cruise',
      description: 'Scenic boat tour along the Potomac with monument views.',
      neighborhood: 'The Wharf / Georgetown',
      distance: '10-20 min',
      website: 'https://www.citycruises.com/washington-dc/',
      tickets: 'https://www.citycruises.com/washington-dc/experiences/',
      needsTickets: true,
      location: 'Capitol View Stays – DC',
      category: 'tours'
    },
    {
      name: 'Capital Bikeshare',
      description: 'Bike rental system with stations throughout DC for self-guided tours.',
      neighborhood: 'City-wide',
      distance: 'Multiple locations',
      website: 'https://www.capitalbikeshare.com/',
      tickets: 'https://account.capitalbikeshare.com/map',
      needsTickets: false,
      location: 'Capitol View Stays – DC',
      category: 'tours'
    },
  ];

  const allPlaces = [...museums, ...sightseeing, ...restaurants, ...familyFriendly, ...nightlife, ...tours];
  const filteredPlaces = activeCategory === 'all' 
    ? allPlaces 
    : allPlaces.filter(place => place.category === activeCategory);

  const getDirectionsLink = (placeName) => {
    return `https://www.google.com/maps/search/${encodeURIComponent(placeName + ' Washington DC')}`;
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1648231150978-27af59c1d3ca"
            alt="Washington DC"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/60 to-slate-900/80"></div>
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 tracking-tight drop-shadow-2xl">
              Local Guide
            </h1>
            <p className="text-xl md:text-2xl text-slate-200 font-light mb-8 max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
              Top picks near our Capitol View Stays — food, museums, sights & experiences
            </p>
          </div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="sticky top-[72px] z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <Button
                  key={cat.id}
                  variant={activeCategory === cat.id ? 'default' : 'outline'}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`${
                    activeCategory === cat.id
                      ? 'bg-slate-900 text-white hover:bg-slate-800'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  } transition-all duration-200`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {cat.name}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Save Guide Notice */}
      <section className="bg-amber-50 border-b border-amber-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Info className="h-5 w-5 text-amber-600" />
              <p className="text-sm text-slate-700">
                <span className="font-semibold">Pro Tip:</span> Bookmark this page for easy access during your stay
              </p>
            </div>
            <Button variant="ghost" className="text-amber-700 hover:bg-amber-100" size="sm">
              <Download className="h-4 w-4 mr-2" />
              PDF Coming Soon
            </Button>
          </div>
        </div>
      </section>

      {/* Nightlife Section - Matching other page styles */}
      {activeCategory === 'nightlife' && (
        <section className="py-16 bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-4">
                Washington DC Nightlife
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Discover the best places to party in Washington DC. From rooftop bars with stunning views to energetic nightclubs, 
                our <Link to="/locations/capitol-hill" className="text-amber-600 hover:text-amber-700 underline">Capitol Hill vacation rentals</Link> put you close to all the action.
              </p>
            </div>

            {/* Top Rooftop Bars Section */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Moon className="h-6 w-6 text-amber-500" />
                Top Rooftop Bars and Party Lounges in Washington DC
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {nightlife.filter(venue => 
                  venue.name.toLowerCase().includes('rooftop') || 
                  venue.name.toLowerCase().includes('pov') ||
                  venue.name.toLowerCase().includes('top of') ||
                  venue.name === 'Onyx Rooftop Lounge'
                ).map((venue, idx) => (
                  <Card key={idx} className="border-slate-200 hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6">
                      <h4 className="text-xl font-bold text-slate-900 mb-2">{venue.name}</h4>
                      <p className="text-slate-600 mb-4 leading-relaxed">{venue.description}</p>
                      <div className="flex flex-wrap gap-3 mb-4 text-sm text-slate-500">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-1 text-slate-400" />
                          <span>{venue.neighborhood}</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1 text-slate-400" />
                          <span>{venue.distance}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {venue.website && (
                          <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" asChild>
                            <a href={venue.website} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Visit Website
                            </a>
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50" asChild>
                          <a href={getDirectionsLink(venue.name)} target="_blank" rel="noopener noreferrer">
                            <Navigation className="h-4 w-4 mr-2" />
                            Directions
                          </a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Nightclubs & Live Music Section */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Nightclubs & Live Music Venues</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {nightlife.filter(venue => 
                  !venue.name.toLowerCase().includes('rooftop') && 
                  !venue.name.toLowerCase().includes('pov') &&
                  !venue.name.toLowerCase().includes('top of') &&
                  venue.name !== 'Onyx Rooftop Lounge'
                ).map((venue, idx) => (
                  <Card key={idx} className="border-slate-200 hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-xl font-bold text-slate-900">{venue.name}</h4>
                        {venue.needsTickets && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                            <Ticket className="h-3 w-3 mr-1" />
                            Tickets
                          </Badge>
                        )}
                      </div>
                      <p className="text-slate-600 mb-4 leading-relaxed">{venue.description}</p>
                      <div className="flex flex-wrap gap-3 mb-4 text-sm text-slate-500">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-1 text-slate-400" />
                          <span>{venue.neighborhood}</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1 text-slate-400" />
                          <span>{venue.distance}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {venue.website && (
                          <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" asChild>
                            <a href={venue.website} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Website
                            </a>
                          </Button>
                        )}
                        {venue.needsTickets && venue.tickets && (
                          <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900" asChild>
                            <a href={venue.tickets} target="_blank" rel="noopener noreferrer">
                              <Ticket className="h-4 w-4 mr-2" />
                              Get Tickets
                            </a>
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50" asChild>
                          <a href={getDirectionsLink(venue.name)} target="_blank" rel="noopener noreferrer">
                            <Navigation className="h-4 w-4 mr-2" />
                            Directions
                          </a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Internal Links for SEO */}
            <div className="mt-12 p-6 bg-amber-50 rounded-xl border border-amber-200">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Stay Near the Nightlife</h3>
              <p className="text-slate-600 mb-4">
                Book a <Link to="/properties" className="text-amber-600 hover:text-amber-700 underline font-medium">Washington DC vacation rental</Link> with 
                Capitol View Stays and enjoy easy access to DC's best nightlife. Our <Link to="/locations/capitol-hill" className="text-amber-600 hover:text-amber-700 underline font-medium">Capitol Hill vacation rentals</Link> are 
                perfectly located for exploring rooftop bars in Washington DC, party lounges in DC, and all the best places to party in Washington DC.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/properties" className="inline-flex items-center text-amber-600 hover:text-amber-700 font-medium">
                  Browse Properties <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
                <Link to="/locations/dupont-circle" className="inline-flex items-center text-amber-600 hover:text-amber-700 font-medium">
                  Dupont Circle Rentals <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
                <Link to="/locations/adams-morgan" className="inline-flex items-center text-amber-600 hover:text-amber-700 font-medium">
                  Adams Morgan Rentals <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Getting Around Section (Transport Category) */}
      {activeCategory === 'transport' && (
        <section className="py-16 bg-slate-50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-serif font-bold text-slate-900 mb-8">Getting Around Washington DC</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Bus className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Metro (Subway)</h3>
                      <p className="text-slate-600 mb-4">
                        DC&apos;s Metro system is the best way to get around. Buy a SmarTrip card or use your phone. 
                        Trains run every 10-15 minutes during peak hours.
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" asChild>
                          <a href="https://www.wmata.com/" target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Metro Website
                          </a>
                        </Button>
                        <Button size="sm" variant="outline" className="border-slate-300" asChild>
                          <a href="https://www.wmata.com/rider-guide/new-riders/" target="_blank" rel="noopener noreferrer">
                            Trip Planner
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Navigation className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Uber & Lyft</h3>
                      <p className="text-slate-600 mb-4">
                        Rideshare services are readily available throughout DC. Perfect for late-night returns 
                        or when Metro isn&apos;t convenient. Expect 5-10 minute wait times.
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" asChild>
                          <a href="https://www.uber.com/" target="_blank" rel="noopener noreferrer">
                            Uber
                          </a>
                        </Button>
                        <Button size="sm" variant="outline" className="border-slate-300" asChild>
                          <a href="https://www.lyft.com/" target="_blank" rel="noopener noreferrer">
                            Lyft
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Parking</h3>
                      <p className="text-slate-600 mb-4">
                        Street parking is available but can be limited. Check signage carefully for restrictions. 
                        Parking garages are available near major attractions. Evening/weekend parking is often easier.
                      </p>
                      <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" asChild>
                        <a href="https://ddot.dc.gov/page/parking-washington-dc" target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Parking Info
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Airports</h3>
                      <p className="text-slate-600 mb-2">
                        <span className="font-semibold">Reagan National (DCA):</span> Closest to DC, 15-20 min Metro ride
                      </p>
                      <p className="text-slate-600 mb-2">
                        <span className="font-semibold">Dulles (IAD):</span> 30-45 min drive, Silver Line Metro available
                      </p>
                      <p className="text-slate-600 mb-4">
                        <span className="font-semibold">BWI:</span> 45-60 min, MARC train or shuttle available
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Places Listings */}
      {activeCategory !== 'transport' && activeCategory !== 'nightlife' && (
        <section className="py-16 bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-4">
                {activeCategory === 'all' ? 'Explore Washington DC' : 
                 activeCategory === 'restaurants' ? 'Best Restaurants in Washington DC' :
                 activeCategory === 'museums' ? 'Top Museums in Washington DC' :
                 activeCategory === 'sightseeing' ? 'Must-See Attractions in Washington DC' :
                 activeCategory === 'family' ? 'Family-Friendly Activities in Washington DC' :
                 activeCategory === 'tours' ? 'Best Tours in Washington DC' :
                 categories.find(c => c.id === activeCategory)?.name}
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                {activeCategory === 'all' && (
                  <>Discover the best of Washington DC. From world-class museums to incredible dining, our <Link to="/locations/capitol-hill" className="text-amber-600 hover:text-amber-700 underline">Capitol Hill vacation rentals</Link> put you close to all the action.</>
                )}
                {activeCategory === 'restaurants' && (
                  <>Discover the best places to eat in Washington DC. From fine dining to local favorites, our <Link to="/locations/capitol-hill" className="text-amber-600 hover:text-amber-700 underline">Capitol Hill vacation rentals</Link> put you close to all the culinary action.</>
                )}
                {activeCategory === 'museums' && (
                  <>Explore world-class museums in Washington DC. From the Smithsonian to hidden gems, our <Link to="/locations/national-mall" className="text-amber-600 hover:text-amber-700 underline">National Mall vacation rentals</Link> put you steps from incredible collections.</>
                )}
                {activeCategory === 'sightseeing' && (
                  <>See the best sights in Washington DC. From iconic monuments to historic landmarks, our <Link to="/locations/capitol-hill" className="text-amber-600 hover:text-amber-700 underline">Capitol Hill vacation rentals</Link> put you close to all the must-see attractions.</>
                )}
                {activeCategory === 'family' && (
                  <>Find the best family activities in Washington DC. From interactive museums to outdoor adventures, our <Link to="/locations/smithsonian" className="text-amber-600 hover:text-amber-700 underline">Smithsonian-area vacation rentals</Link> are perfect for families with kids.</>
                )}
                {activeCategory === 'tours' && (
                  <>Discover the best tours in Washington DC. From monuments by night to hidden history, our <Link to="/locations/capitol-hill" className="text-amber-600 hover:text-amber-700 underline">Capitol Hill vacation rentals</Link> make the perfect home base for exploring.</>
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredPlaces.map((place, idx) => (
                <Card key={idx} className="border-slate-200 hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-xl font-bold text-slate-900 pr-4">{place.name}</h3>
                      {place.needsTickets && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 flex-shrink-0">
                          <Ticket className="h-3 w-3 mr-1" />
                          Tickets
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-slate-600 mb-4 leading-relaxed">{place.description}</p>
                    
                    <div className="flex flex-wrap gap-3 mb-4 text-sm text-slate-600">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1 text-slate-400" />
                        <span>{place.neighborhood}</span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1 text-slate-400" />
                        <span>{place.distance}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {place.website && (
                        <Button 
                          size="sm" 
                          className="bg-slate-900 hover:bg-slate-800 text-white"
                          asChild
                        >
                          <a href={place.website} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Website
                          </a>
                        </Button>
                      )}
                      
                      {place.needsTickets && place.tickets && (
                        <Button 
                          size="sm" 
                          className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                          asChild
                        >
                          <a href={place.tickets} target="_blank" rel="noopener noreferrer">
                            <Ticket className="h-4 w-4 mr-2" />
                            Get Tickets
                          </a>
                        </Button>
                      )}
                      
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="border-slate-300 text-slate-700 hover:bg-slate-50"
                        asChild
                      >
                        <a href={getDirectionsLink(place.name)} target="_blank" rel="noopener noreferrer">
                          <Navigation className="h-4 w-4 mr-2" />
                          Directions
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Internal Links for SEO - Added to all category pages */}
            <div className="mt-12 p-6 bg-amber-50 rounded-xl border border-amber-200">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Stay Close to Everything</h3>
              <p className="text-slate-600 mb-4">
                Book a <Link to="/properties" className="text-amber-600 hover:text-amber-700 underline font-medium">Washington DC vacation rental</Link> with 
                Capitol View Stays and enjoy easy access to DC's best attractions. Our <Link to="/locations/capitol-hill" className="text-amber-600 hover:text-amber-700 underline font-medium">Capitol Hill vacation rentals</Link> are 
                perfectly located near the National Mall, Smithsonian museums, and all the best things to do in Washington DC.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/properties" className="inline-flex items-center text-amber-600 hover:text-amber-700 font-medium">
                  Browse Properties <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
                <Link to="/locations/national-mall" className="inline-flex items-center text-amber-600 hover:text-amber-700 font-medium">
                  National Mall Rentals <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
                <Link to="/locations/smithsonian" className="inline-flex items-center text-amber-600 hover:text-amber-700 font-medium">
                  Smithsonian Area Rentals <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Disclaimer Footer */}
      <section className="py-8 bg-slate-100 border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-slate-600">
            <span className="font-semibold">Note:</span> Recommendations are subject to change. 
            Please check hours, ticket availability, and current policies before visiting. 
            Some attractions require advance reservations.
          </p>
        </div>
      </section>
    </div>
  );
};

export default LocalGuide;
